import { RefObject, useEffect, useState } from "react";

import ResizeObserver from "resize-observer-polyfill";

type UseIsScrollingConfig<T> = {
  ref: RefObject<T | null>;
  callback?: ({ x, y }: { x: boolean; y: boolean }) => void;
};

const checkIsScrolling = (element: HTMLElement): { x: boolean; y: boolean } => {
  return {
    x: element.scrollWidth > element.clientWidth,
    y: element.scrollHeight > element.clientHeight,
  };
};

export const useIsScrolling = <T extends HTMLElement>({ ref, callback }: UseIsScrollingConfig<T>) => {
  const [isScrolling, setIsScrolling] = useState<{ x: boolean; y: boolean } | undefined>();

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const scheduleScrollingUpdate = () => {
      requestAnimationFrame(() => {
        if (ref.current) {
          const result = checkIsScrolling(ref.current);
          setIsScrolling(result);
          callback?.(result);
        }
      });
    };

    // Check on mount
    scheduleScrollingUpdate();

    // Check on window resize
    window.addEventListener("resize", scheduleScrollingUpdate);

    // Check when element size changes
    const resizeObserver = new ResizeObserver(scheduleScrollingUpdate);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      window.removeEventListener("resize", scheduleScrollingUpdate);
      resizeObserver.disconnect();
    };
  }, [ref, callback]);

  return isScrolling;
};
